import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  adminApi,
  type DashboardStats,
  type AnalyticsSummary,
} from "@/lib/superadmin";
import { PLAN_LABEL } from "@/lib/access";
import {
  PageHeader,
  StatCard,
  SectionCard,
  Card,
  ErrorBanner,
  PageSkeleton,
  EmptyState,
  IconButton,
  AreaChart,
  BarChart,
  DonutChart,
  ProgressRows,
} from "../components/ui";

/** ₹ formatter (INR, compact for large values). */
function inr(value: number): string {
  if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value.toLocaleString("en-IN")}`;
}

function relTime(at?: string): string {
  if (!at) return "—";
  const d = new Date(at);
  if (isNaN(d.getTime())) return at;
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

/** % change between the last two points of a series. */
function trendDelta(series: number[]): { delta: string; up: boolean } | null {
  if (series.length < 2) return null;
  const prev = series[series.length - 2];
  const curr = series[series.length - 1];
  if (prev === 0) return null;
  const pct = ((curr - prev) / prev) * 100;
  return { delta: `${Math.abs(pct).toFixed(1)}%`, up: pct >= 0 };
}

const QUICK_ACTIONS = [
  { label: "Add tenant", icon: "ri-store-2-line", to: "/superadmin/tenants", tone: "bg-primary-50 text-primary-600" },
  { label: "New coupon", icon: "ri-coupon-3-line", to: "/superadmin/coupons", tone: "bg-amber-50 text-amber-600" },
  { label: "Announce", icon: "ri-megaphone-line", to: "/superadmin/announcements", tone: "bg-violet-50 text-violet-600" },
  { label: "Edit plans", icon: "ri-price-tag-3-line", to: "/superadmin/plans", tone: "bg-emerald-50 text-emerald-600" },
  { label: "Feature flags", icon: "ri-toggle-line", to: "/superadmin/entitlements", tone: "bg-sky-50 text-sky-600" },
  { label: "Audit log", icon: "ri-shield-check-line", to: "/superadmin/system", tone: "bg-rose-50 text-rose-600" },
];

const ACTIVITY_ICONS: Record<string, { icon: string; tone: string }> = {
  signup: { icon: "ri-user-add-line", tone: "bg-emerald-50 text-emerald-600" },
  order: { icon: "ri-shopping-bag-3-line", tone: "bg-amber-50 text-amber-600" },
  payment: { icon: "ri-bank-card-line", tone: "bg-primary-50 text-primary-600" },
  subscription: { icon: "ri-vip-crown-2-line", tone: "bg-violet-50 text-violet-600" },
};

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, a] = await Promise.all([
        adminApi.dashboard.stats(),
        adminApi.analytics.summary().catch(() => null),
      ]);
      setStats(s);
      setAnalytics(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const t = stats?.tenants;
  const revSeries = analytics?.revenueTrend.map((r) => r.revenue) ?? [];
  const signSeries = analytics?.signupTrend.map((s) => s.count) ?? [];
  const revDelta = trendDelta(revSeries);
  const signDelta = trendDelta(signSeries);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      <PageHeader
        title="Platform Dashboard"
        subtitle="Overview of all restaurant tenants, revenue, and platform activity"
        actions={
          <IconButton
            icon="ri-refresh-line"
            title="Refresh"
            spinning={loading}
            onClick={() => void load()}
          />
        }
      />

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total Tenants"
              value={t ? t.total.toLocaleString("en-IN") : "—"}
              icon="ri-store-2-line"
              tone="primary"
              hint={t ? `${t.active} active` : undefined}
            />
            <StatCard
              label="Monthly Recurring Revenue"
              value={stats ? inr(stats.mrr) : "—"}
              icon="ri-funds-line"
              tone="emerald"
              delta={revDelta?.delta}
              deltaUp={revDelta?.up}
            />
            <StatCard
              label="New Signups (30d)"
              value={stats ? stats.newSignups30d.toLocaleString("en-IN") : "—"}
              icon="ri-user-add-line"
              tone="violet"
              delta={signDelta?.delta}
              deltaUp={signDelta?.up}
            />
            <StatCard
              label="Orders Today"
              value={stats ? stats.ordersToday.toLocaleString("en-IN") : "—"}
              icon="ri-shopping-bag-3-line"
              tone="amber"
            />
          </div>

          {/* Quick actions */}
          <Card className="p-3">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {QUICK_ACTIONS.map((a) => (
                <Link
                  key={a.label}
                  to={a.to}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                >
                  <span
                    className={`w-9 h-9 flex items-center justify-center rounded-lg ${a.tone} group-hover:scale-110 transition-transform`}
                  >
                    <i className={`${a.icon}`}></i>
                  </span>
                  <span className="text-[11px] font-medium text-foreground-600 whitespace-nowrap">
                    {a.label}
                  </span>
                </Link>
              ))}
            </div>
          </Card>

          {/* Trends */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Revenue Trend" icon="ri-funds-line">
              {analytics?.revenueTrend.length ? (
                <AreaChart
                  data={revSeries}
                  labels={analytics.revenueTrend.map((r) => r.month)}
                  format={inr}
                />
              ) : (
                <EmptyState
                  icon="ri-line-chart-line"
                  title="No revenue data yet"
                  hint="Revenue will appear once tenants start subscribing."
                />
              )}
            </SectionCard>

            <SectionCard title="New Signups" icon="ri-user-add-line">
              {analytics?.signupTrend.length ? (
                <BarChart
                  data={signSeries}
                  labels={analytics.signupTrend.map((s) => s.month)}
                  colorClass="bg-emerald-400 hover:bg-emerald-500"
                />
              ) : (
                <EmptyState
                  icon="ri-user-add-line"
                  title="No signup data yet"
                  hint="New tenant signups will chart here."
                />
              )}
            </SectionCard>
          </div>

          {/* Status + plans */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Tenant Status" icon="ri-store-2-line">
              {t ? (
                <ProgressRows
                  rows={[
                    { label: "Active", value: t.active, colorClass: "bg-emerald-500" },
                    { label: "Trial", value: t.trial, colorClass: "bg-amber-400" },
                    { label: "Suspended", value: t.suspended, colorClass: "bg-slate-400" },
                    { label: "Churned", value: t.churned, colorClass: "bg-rose-400" },
                  ]}
                />
              ) : (
                <EmptyState title="No tenant data" />
              )}
            </SectionCard>

            <SectionCard title="Plan Distribution" icon="ri-price-tag-3-line">
              {analytics?.planDistribution.length ? (
                <DonutChart
                  segments={analytics.planDistribution.map((p) => ({
                    label: PLAN_LABEL[p.plan] ?? p.plan,
                    value: p.count,
                  }))}
                  centerValue={String(
                    analytics.planDistribution.reduce((s, p) => s + p.count, 0),
                  )}
                  centerLabel="tenants"
                />
              ) : (
                <EmptyState
                  icon="ri-pie-chart-2-line"
                  title="No plan data yet"
                  hint="Distribution across Free / Pro / Ultra will show here."
                />
              )}
            </SectionCard>
          </div>

          {/* Recent activity */}
          <SectionCard
            title="Recent Activity"
            icon="ri-pulse-line"
            action={
              <Link
                to="/superadmin/system"
                className="text-xs font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                View audit log →
              </Link>
            }
          >
            {stats?.activity.length ? (
              <div className="divide-y divide-background-100">
                {stats.activity.map((a, i) => {
                  const meta =
                    ACTIVITY_ICONS[(a.type ?? "").toLowerCase()] ?? {
                      icon: "ri-pulse-line",
                      tone: "bg-primary-50 text-primary-600",
                    };
                  return (
                    <div key={i} className="flex items-center justify-between py-2.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${meta.tone}`}
                        >
                          <i className={`${meta.icon} text-sm`}></i>
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground-800 font-medium truncate capitalize">
                            {a.type ?? "event"}
                          </p>
                          <p className="text-xs text-foreground-400 truncate">
                            {a.detail ?? a.restaurantId ?? "—"}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">
                        {relTime(a.at)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                icon="ri-pulse-line"
                title="No recent activity"
                hint="Signups, orders, and payments will stream in here."
              />
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
